import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Award, Clock, Lightbulb, Globe, ArrowRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Experience = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const features = [
    { key: 'quality', icon: Award },
    { key: 'timely', icon: Clock },
    { key: 'innovative', icon: Lightbulb },
    { key: 'global', icon: Globe },
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="experience"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
      data-testid="experience-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#1E293B]"></div>
      
      {/* Decorative Background Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://images.pexels.com/photos/19233057/pexels-photo-19233057.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Robot Arm"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div>
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 mb-6"
            >
              <div className="h-px w-12 bg-[#6B8CAE]"></div>
              <span className="text-[#6B8CAE] text-sm font-mono uppercase tracking-wider">
                {t('experience.badge')}
              </span>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
              data-testid="experience-title"
            >
              {t('experience.title')}{' '}
              <span className="text-[#6B8CAE]">{t('experience.titleHighlight')}</span>
            </motion.h2>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-[#94A3B8] text-lg mb-10 leading-relaxed"
            >
              {t('experience.subtitle')}
            </motion.p>

            {/* CTA */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              onClick={scrollToContact}
              className="btn-primary flex items-center space-x-2 group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="experience-cta"
            >
              <span>{t('contact.form.submit')}</span>
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </div>

          {/* Right Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                className="card-technical p-6 group"
                data-testid={`feature-${feature.key}`}
              >
                {/* Icon */}
                <div className="w-14 h-14 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center mb-5 group-hover:bg-[#6B8CAE]/20 group-hover:border-[#6B8CAE] transition-all duration-300">
                  <feature.icon size={28} className="text-[#6B8CAE]" />
                </div>

                {/* Content */}
                <h3 className="text-white text-lg font-semibold mb-2 group-hover:text-[#6B8CAE] transition-colors">
                  {t(`experience.features.${feature.key}.title`)}
                </h3>
                <p className="text-[#94A3B8] text-sm leading-relaxed">
                  {t(`experience.features.${feature.key}.description`)}
                </p>

                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-[#6B8CAE]/30 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Bottom Stats Bar */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 p-8 bg-[#0F172A]/50 border border-[#6B8CAE]/20"
        >
          {[
            { value: 'ISO', label: 'Certified Standards' },
            { value: '24/7', label: 'Support Available' },
            { value: '100%', label: 'Client Satisfaction' },
            { value: 'EU', label: 'Based Operations' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-2xl md:text-3xl font-bold text-[#6B8CAE] font-mono">
                {stat.value}
              </div>
              <div className="text-[#94A3B8] text-sm mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
